import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  IconButton,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { useReporting } from '@/reporting';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';

interface MarketActionsMenuProps {
  takerMarket: TakerMarket;
}

const MarketActionsMenu = ({ takerMarket }: MarketActionsMenuProps) => {
  const { t } = useTranslation();
  const { openModal, openAutoRenewalModal, openDeleteScheduleModal } = useOfferModalContext();
  const { trackEnterpriseEvent } = useReporting();
  const { getRestrictions } = useRestrictions();
  const { canEditInvoices, canEditOffers } = getRestrictions([takerMarket]);
  const { makerOrganizationUuid, marketId, takerDivisionId } = takerMarket;

  const hasPreferredOffer = takerMarket.offerConfig.offerType === 'PREFERRED_TERM';
  const isManuallyAwardedWithActiveOffer = takerMarket.offerConfig.isManuallyAwarded;
  const hasActiveExpressAccept = takerMarket.offerConfig.isActiveExpressAccept;

  const canModifyOffer = canEditOffers && !isManuallyAwardedWithActiveOffer && !hasActiveExpressAccept;
  const canModifyInvoices = canEditInvoices && !isManuallyAwardedWithActiveOffer && !hasActiveExpressAccept;
  const canModifySchedule = canEditOffers && !isManuallyAwardedWithActiveOffer && !hasActiveExpressAccept;

  const handleModifyOffer = () => {
    openModal({ takerMarket, initialAccordionValue: 'Rate' });
    trackEnterpriseEvent('market-actions-menu::edit-offer::clicked', { marketId, takerDivisionId });
  };

  const handleEditInvoices = () => {
    trackEnterpriseEvent('market-actions-menu::edit-invoices::clicked', { marketId, takerDivisionId });
  };

  const handleViewHistory = () => {
    trackEnterpriseEvent('market-actions-menu::view-history::clicked', { marketId, takerDivisionId });
  };

  const handleModifySchedule = () => {
    openModal({ takerMarket, initialAccordionValue: 'Frequency' });
    trackEnterpriseEvent('market-actions-menu::modify-schedule::clicked', { marketId, takerDivisionId });
  };

  const handleDeleteSchedule = () => {
    openDeleteScheduleModal(takerMarket);
    trackEnterpriseEvent('market-actions-menu::delete-schedule::clicked', { marketId, takerDivisionId });
  };

  const handleAutoRenewal = () => {
    openAutoRenewalModal(takerMarket);
    trackEnterpriseEvent('market-actions-menu::auto-renewal::clicked', { marketId, takerDivisionId });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton icon={EllipsisVerticalIcon} name="actions" size="sm" variant="ancillary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!hasPreferredOffer &&
            (canModifyOffer && takerMarket.marketIsEnabled ? (
              <DropdownMenuItem onClick={handleModifyOffer}>{t('core.modifyOffer')}</DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled>{t('core.modifyOffer')}</DropdownMenuItem>
            ))}
          {canModifyInvoices ? (
            <DropdownMenuItem asChild onClick={handleEditInvoices}>
              <Link
                to={asEnterpriseLink(
                  `customers/${makerOrganizationUuid}/markets/${marketId}/division/${takerDivisionId}/invoices/eligible`
                )}
              >
                {t('core.editInvoices')}
              </Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled>{t('core.editInvoices')}</DropdownMenuItem>
          )}
          {!hasPreferredOffer &&
            (canModifySchedule && takerMarket.marketIsEnabled ? (
              <DropdownMenuItem onClick={handleModifySchedule}>{t('scheduling.modifySchedule')}</DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled>{t('scheduling.modifySchedule')}</DropdownMenuItem>
            ))}
          {canModifySchedule && !!takerMarket.offerSchedule && (
            <DropdownMenuItem onClick={handleDeleteSchedule}>{t('automation.deleteSchedule')}</DropdownMenuItem>
          )}
          {hasPreferredOffer && (
            <DropdownMenuItem onClick={handleAutoRenewal}>{t('offer.autoRenew.modifyRenewal')}</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link
              onClick={handleViewHistory}
              to={asEnterpriseLink(
                `customers/${makerOrganizationUuid}/markets/${marketId}/division/${takerDivisionId}/history`
              )}
            >
              {t('core.viewHistory')}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default MarketActionsMenu;
